import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Locate, Star } from "lucide-react";

// Fix default marker icons (Leaflet's default icons reference relative URLs
// that break under webpack/CRA bundling).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Compact, kebab-friendly Lucide-styled SVG marker for vendors (orange).
function makeVendorIcon(featured = false) {
  const color = featured ? "#f97316" : "#dc2626";
  return L.divIcon({
    className: "sb-vendor-marker",
    html: `<div style="
      transform: translate(-50%, -100%);
      background: ${color};
      width: 28px; height: 28px;
      border-radius: 50% 50% 50% 0;
      transform-origin: center;
      border: 2px solid #fff;
      box-shadow: 0 3px 6px rgba(0,0,0,.25);
      display:flex; align-items:center; justify-content:center;
      transform: rotate(-45deg);
    ">
      <div style="transform: rotate(45deg); color:#fff; font-weight:700; font-size:11px;">✦</div>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

function FitToVendors({ vendors, userPos }) {
  const map = useMap();
  useEffect(() => {
    const points = vendors.filter((v) => v.loc).map((v) => [v.loc.lat, v.loc.lng]);
    if (userPos) points.push([userPos.lat, userPos.lng]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
    }
  }, [vendors, userPos, map]);
  return null;
}

/** Map of one or many vendors. Renders a per-vendor pin and a "Use my
 * location" control that drops a blue dot for the device location.
 *
 * @param vendors Array of vendor docs (each MUST have a `loc:{lat,lng}` field
 *                — populated by the backend via cities_latlng.py).
 * @param height  CSS height for the map container.
 */
export default function VendorMap({ vendors, height = 320 }) {
  const [userPos, setUserPos] = useState(null);
  const [error, setError] = useState("");
  const triedRef = useRef(false);

  const valid = useMemo(() => (vendors || []).filter((v) => v?.loc), [vendors]);
  const center = useMemo(() => {
    if (valid[0]?.loc) return [valid[0].loc.lat, valid[0].loc.lng];
    return [19.7515, 75.7139]; // Maharashtra centroid as fallback
  }, [valid]);

  const askLocation = () => {
    if (!navigator.geolocation) { setError("Geolocation not supported"); return; }
    triedRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setError(err.code === 1 ? "Location permission denied" : "Could not get location"),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60_000 }
    );
  };

  // Auto-attempt once if permission was previously granted (no prompt thrown).
  useEffect(() => {
    if (navigator.permissions && !triedRef.current) {
      navigator.permissions.query({ name: "geolocation" }).then((p) => {
        if (p.state === "granted") askLocation();
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (valid.length === 0) {
    return (
      <div
        data-testid="vendor-map-empty"
        className="rounded-2xl border border-dashed border-border/60 text-sm text-muted-foreground flex items-center justify-center"
        style={{ height }}
      >
        No mappable vendors yet.
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl overflow-hidden border border-border/60" style={{ height }} data-testid="vendor-map">
      <MapContainer center={center} zoom={6} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToVendors vendors={valid} userPos={userPos} />
        <MarkerClusterGroup
          chunkedLoading
          showCoverageOnHover={false}
          maxClusterRadius={45}
          spiderfyOnEveryZoom={false}
          spiderfyOnMaxZoom
        >
          {valid.map((v) => (
            <Marker key={v.vendor_id} position={[v.loc.lat, v.loc.lng]} icon={makeVendorIcon(v.is_verified)}>
              <Popup>
                <div className="space-y-1 min-w-[180px]" data-testid={`vendor-map-popup-${v.vendor_id}`}>
                  <div className="font-semibold">{v.business_name}</div>
                  <div className="text-xs text-muted-foreground">{v.category} · {v.city}</div>
                  {v.rating > 0 && (
                    <div className="flex items-center gap-1 text-xs"><Star className="h-3 w-3 fill-current text-amber-500" /> {v.rating.toFixed(1)} ({v.rating_count})</div>
                  )}
                  <Link to={`/vendors/${v.vendor_id}`} className="text-xs text-primary underline">View profile →</Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
        {userPos && (
          <CircleMarker
            center={[userPos.lat, userPos.lng]}
            radius={9}
            pathOptions={{ color: "#2563eb", fillColor: "#3b82f6", fillOpacity: 0.85, weight: 3 }}
            data-testid="vendor-map-user-pos"
          >
            <Popup>You are here</Popup>
          </CircleMarker>
        )}
      </MapContainer>

      <div className="absolute top-3 right-3 z-[400] flex flex-col gap-2 items-end">
        <Button
          size="sm"
          variant="secondary"
          onClick={askLocation}
          className="rounded-full shadow-sm"
          data-testid="vendor-map-locate-btn"
        >
          <Locate className="h-3.5 w-3.5 mr-1" /> Use my location
        </Button>
        {error && (
          <div className="text-[11px] bg-background/90 backdrop-blur px-2 py-1 rounded-md border border-border/60 text-destructive max-w-[200px]" data-testid="vendor-map-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
